import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SearchIcon, ShoppingCartIcon, MapPinIcon, ChevronDownIcon, HeartIcon, TrashIcon, XIcon, ChevronRightIcon, TrendingUpIcon, ShoppingBagIcon, TagIcon, HeartPulseIcon, ActivityIcon, BrainIcon, ThermometerIcon, PillIcon, StethoscopeIcon, GlobeIcon, UserIcon, PackageIcon, LogOutIcon, ClockIcon, MenuIcon } from 'lucide-react';
import { LoyaltyCard } from './account/LoyaltyCard';
import { LoyaltyOnboardingBottomSheet } from './modals/LoyaltyOnboardingBottomSheet';
import { AuthBottomSheet } from './modals/AuthBottomSheet';
import { LanguageBottomSheet } from './modals/LanguageBottomSheet';
import { useStore } from '../lib/store';
import { POPULAR_SEARCHES, TRENDING_SEARCHES, TRENDING_PRODUCTS } from '../data/searchData';
// Debounce utility
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);
  return debouncedValue;
}
interface MobileHomeHeaderProps {
  selectedLocation: {
    name: string;
  } | null;
  onOpenLocationModal: () => void;
  alwaysWhiteBackground?: boolean;
  transparentAtTop?: boolean;
}
export function MobileHomeHeader({
  selectedLocation,
  onOpenLocationModal,
  alwaysWhiteBackground = false,
  transparentAtTop = false
}: MobileHomeHeaderProps) {
  const navigate = useNavigate();
  const {
    cart: cartItems,
    wishlist: wishlistItems,
    cartCount,
    cartTotal,
    language,
    setLanguage,
    searchQuery,
    setSearchQuery,
    recentSearches,
    addRecentSearch,
    clearRecentSearches
  } = useStore();
  const [isScrolled, setIsScrolled] = useState(false);
  const [showCartMenu, setShowCartMenu] = useState(false);
  const [showCategoriesMenu, setShowCategoriesMenu] = useState(false);
  const currentCartCount = cartCount();
  const currentWishlistCount = wishlistItems.length;
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchResultsLimit, setSearchResultsLimit] = useState(5);
  const cartMenuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  // Check if selected location is a pickup branch
  const isPickupLocation = selectedLocation && 'distance' in selectedLocation;
  // States for bottom sheets
  const [languageBottomSheetOpen, setLanguageBottomSheetOpen] = useState(false);
  const [authBottomSheetOpen, setAuthBottomSheetOpen] = useState(false);
  const [loyaltyBottomSheetOpen, setLoyaltyBottomSheetOpen] = useState(false);
  const [loyaltyProgress, setLoyaltyProgress] = useState(0);
  const [loyaltyData, setLoyaltyData] = useState<{
    memberId?: string;
    pointsBalance?: number;
    status?: 'active' | 'inactive';
    tier?: 'silver' | 'gold' | 'platinum';
    isEnrolled?: boolean;
  }>({});
  // Calculate loyalty progress from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('loyaltyOnboarding');
    if (savedData) {
      const data = JSON.parse(savedData);
      const requiredFields = [data.gender, data.maritalStatus, data.nationality, data.dateOfBirth, data.workingSector, data.city, data.district, data.preferredLanguage];
      const completedRequired = requiredFields.filter(field => field !== '').length;
      const progress = Math.round(completedRequired / requiredFields.length * 100);
      setLoyaltyProgress(progress);
      if (progress === 100) {
        setLoyaltyData({
          memberId: '1234567890',
          pointsBalance: 3250,
          status: 'active',
          tier: 'gold',
          isEnrolled: true
        });
      } else {
        setLoyaltyData({
          isEnrolled: false
        });
      }
    }
  }, [loyaltyBottomSheetOpen]);
  const openAuthModal = () => {
    setAuthBottomSheetOpen(true);
  };
  const handleLanguageChange = (lang: string) => {
    setLanguage(lang as 'en' | 'ar');
  };
  const handleAuthSuccess = (isNewUser: boolean) => {
    setAuthBottomSheetOpen(false);
    if (isNewUser && loyaltyProgress < 100) {
      setTimeout(() => setLoyaltyBottomSheetOpen(true), 500);
    }
  };
  const handleLoyaltyComplete = (data: any) => {
    localStorage.setItem('loyaltyOnboarding', JSON.stringify(data));
    setLoyaltyProgress(100);
    setLoyaltyBottomSheetOpen(false);
    setLoyaltyData({
      memberId: '1234567890',
      pointsBalance: 0,
      status: 'active',
      tier: 'silver',
      isEnrolled: true
    });
  };
  const handleSearchFocus = () => {
    setSearchFocused(true);
    setShowSearchResults(true);
  };
  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, [setSearchQuery]);
  useEffect(() => {
    if (debouncedSearchQuery) {
      setShowSearchResults(true);
    }
  }, [debouncedSearchQuery]);
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      addRecentSearch(searchQuery.trim());
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setShowSearchResults(false);
      setSearchFocused(false);
    }
  };
  const handleSearchSelect = (query: string) => {
    setSearchQuery(query);
    navigate(`/search?q=${encodeURIComponent(query)}`);
    setShowSearchResults(false);
    setSearchFocused(false);
  };
  const handleProductClick = (productId: number) => {
    navigate(`/product/${productId}`);
    setShowSearchResults(false);
    setSearchFocused(false);
  };
  const currentCartTotal = cartTotal();
  const conditions = [{
    icon: <HeartPulseIcon className="w-4 h-4 text-red-600" data-id="element-3180" data-mp="19lxoe84gex19" />,
    name: 'Heart Health',
    slug: 'heart-health'
  }, {
    icon: <ActivityIcon className="w-4 h-4 text-blue-600" data-id="element-3181" data-mp="14o5l6hyczohr" />,
    name: 'Diabetes',
    slug: 'diabetes'
  }, {
    icon: <BrainIcon className="w-4 h-4 text-purple-600" data-id="element-3182" data-mp="zukvreau02u6" />,
    name: 'Mental Health',
    slug: 'mental-health'
  }, {
    icon: <ThermometerIcon className="w-4 h-4 text-orange-600" data-id="element-3183" data-mp="qvppk27sesqv" />,
    name: 'Pain Relief',
    slug: 'pain-relief'
  }, {
    icon: <PillIcon className="w-4 h-4 text-green-600" data-id="element-3184" data-mp="lvl5k27o7fvw" />,
    name: 'Digestive Health',
    slug: 'digestive-health'
  }, {
    icon: <StethoscopeIcon className="w-4 h-4 text-cyan-600" data-id="element-3185" data-mp="gzmbpz3kp5o3" />,
    name: 'Respiratory',
    slug: 'respiratory'
  }];
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY >= 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (cartMenuRef.current && !cartMenuRef.current.contains(event.target as Node)) {
        setShowCartMenu(false);
      }
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        // Only close if not focused on input
        if (!searchFocused && !searchQuery) {
          setShowSearchResults(false);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [searchFocused, searchQuery]);
  const isSearchActive = showSearchResults || searchFocused;
  useEffect(() => {
    if (typeof document !== 'undefined' && document.body) {
      document.body.style.overflow = isSearchActive ? 'hidden' : 'auto';
    }
    return () => {
      if (typeof document !== 'undefined' && document.body) {
        document.body.style.overflow = 'auto';
      }
    };
  }, [isSearchActive]);
  const handleCategoryClick = (slug: string) => {
    setShowCategoriesMenu(false);
    navigate(`/category/${slug}`);
  };
  const handleConditionClick = (slug: string) => {
    setShowCategoriesMenu(false);
    navigate(`/category/${slug}`);
  };
  return <>
      <div className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 m-0 ${alwaysWhiteBackground ? 'bg-white shadow-lg border-b border-gray-200' : isScrolled ? 'bg-white shadow-lg border-b border-gray-200' : transparentAtTop ? isSearchActive ? 'bg-white border-b border-gray-100' : 'bg-transparent' : 'bg-white border-b border-gray-200'}`} style={{
      paddingTop: 'env(safe-area-inset-top)'
    }} data-id="element-3186" data-mp="f68w6u94to1d">
        {!isSearchActive && <div className={`mx-4 transition-all duration-300 ${alwaysWhiteBackground ? 'opacity-100 mt-2 mb-2' : isScrolled ? 'opacity-0 h-0 overflow-hidden mb-0 mt-0 pointer-events-none' : transparentAtTop ? 'opacity-100 mt-2 mb-1' : 'opacity-100 mt-2 mb-2'}`} data-id="element-3187" data-mp="34npu6wqnwda6">
            <button onClick={onOpenLocationModal} className={`w-full p-2.5 rounded-xl flex items-center justify-between transition-all ${alwaysWhiteBackground ? 'bg-gray-50 border border-gray-200' : 'bg-white shadow-md'}`} data-id="element-3188" data-mp="2uk2mn8rshtqm">
              <div className="flex items-center gap-2 min-w-0 flex-1 overflow-hidden" data-id="element-3189" data-mp="2lpwc2at8ai7m">
                <MapPinIcon className="w-4 h-4 text-[#1E2B55] flex-shrink-0" data-id="element-3190" data-mp="26tf0bih4n1kj" />
                <span className="text-sm min-w-0 flex-1 truncate overflow-hidden" data-id="element-3191" data-mp="2buma1n2tyatu">
                  <span className="text-gray-600" data-id="element-3192" data-mp="1xutoe665uksq">
                    {isPickupLocation ? 'Pickup from: ' : 'Deliver to: '}
                  </span>
                  <span className="font-semibold text-gray-900" data-id="element-3193" data-mp="22v90yvimbo9n">
                    {selectedLocation?.name || 'Select Location'}
                  </span>
                </span>
              </div>
              <ChevronDownIcon className="w-4 h-4 text-[#1E2B55] flex-shrink-0 ml-1" data-id="element-3194" data-mp="zp2x6pfas9n" />
            </button>
          </div>}
        <div className={`flex items-center gap-2.5 px-4 transition-all duration-300 ${isSearchActive ? 'py-2' : alwaysWhiteBackground ? 'pb-2' : isScrolled ? 'py-3.5' : transparentAtTop ? 'pb-3' : 'py-3'}`} data-id="element-3195" data-mp="3qjkhtv8kv5lk">
          {isSearchActive && <button onClick={() => {
          setShowSearchResults(false);
          setSearchFocused(false);
        }} className="p-1 -ml-1 text-gray-500 hover:text-gray-700" data-id="element-3196" data-mp="3gry7e142dajv">
              <ChevronRightIcon className="w-6 h-6 rotate-180" data-id="element-3197" data-mp="3vj71amf01i2t" />
            </button>}
          <div className="flex-1 relative" ref={searchRef} data-id="element-3198" data-mp="4cdu91xiyoqd">
            <form onSubmit={handleSearchSubmit} data-id="element-3199" data-mp="4at03df4h7t1">
              <div className={`relative rounded-full transition-all ${isSearchActive ? 'bg-gray-100' : alwaysWhiteBackground || isScrolled ? 'bg-white border border-gray-300' : 'bg-white shadow-lg'}`} data-id="element-3200" data-mp="2k38me1x541to">
                <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 pointer-events-none z-10" data-id="element-3201" data-mp="2gk9jdmgc8vk0" />
                <input type="text" placeholder="Search" value={searchQuery} onChange={e => handleSearchChange(e.target.value)} onFocus={handleSearchFocus} className="w-full pl-12 pr-10 py-3 rounded-full text-sm text-gray-900 placeholder-gray-500 focus:outline-none bg-transparent" data-id="element-3202" data-mp="2qk0enqcq3el5" />
                {searchQuery && <button type="button" onClick={() => {
                setSearchQuery('');
              }} className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10" data-id="element-3203" data-mp="27lxxfxqf2lxw">
                    <XIcon className="w-4 h-4" data-id="element-3204" data-mp="1a4z7yn3w9olz" />
                  </button>}
              </div>
            </form>
          </div>
          {!isSearchActive && <>
              <button type="button" onClick={() => navigate('/menu')} aria-label="Open menu" className={`rounded-xl bg-white hover:bg-gray-50 transition-all flex-shrink-0 p-3 ${alwaysWhiteBackground || isScrolled ? 'border border-gray-300' : 'shadow-lg'}`} data-id="element-3205" data-mp="3f5tikb9zuctw">
                <MenuIcon className="text-[#1E2B55] w-6 h-6" strokeWidth={2} data-id="element-3206" data-mp="2rbie0hzc6koe" />
              </button>
              <div className="relative flex-shrink-0" ref={cartMenuRef} data-id="element-3207" data-mp="2wa3fekqbraef">
                <button onClick={() => setShowCartMenu(!showCartMenu)} className={`rounded-xl bg-white hover:bg-gray-50 transition-all relative flex-shrink-0 p-3 ${alwaysWhiteBackground || isScrolled ? 'border border-gray-300' : 'shadow-lg'}`} data-id="element-3208" data-mp="308ankr6q33e0">
                  <ShoppingCartIcon className="text-[#1E2B55] w-6 h-6" strokeWidth={2} data-id="element-3209" data-mp="kh4xembzz7hd" />
                  {currentCartCount > 0 && <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center shadow-sm" data-id="element-3210" data-mp="po3ay85yt7m5">
                      {currentCartCount}
                    </span>}
                </button>
                {showCartMenu && <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50" data-id="element-3211" data-mp="bu275005ouex">
                    <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center" data-id="element-3212" data-mp="1xpe84cfqkl8">
                      <div data-id="element-3213" data-mp="3u29siggv3smd">
                        <p className="text-sm font-medium text-gray-900" data-id="element-3214" data-mp="3u332dqb4ay8q">
                          Shopping Cart
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5" data-id="element-3215" data-mp="1w9nng6aqvgh4">
                          {cartItems.reduce((total, item) => total + item.quantity, 0)}{' '}
                          items
                        </p>
                      </div>
                      <button onClick={() => {
                  setShowCartMenu(false);
                  navigate('/cart');
                }} className="text-xs font-medium text-[#1E2B55] hover:underline" data-id="element-3216" data-mp="1qdga5vkfvsn5">
                        View Cart
                      </button>
                    </div>
                    {cartItems.length > 0 ? <>
                        <div className="py-2 max-h-80 overflow-y-auto" data-id="element-3217" data-mp="16n32057q2x70">
                          {cartItems.map(item => <div key={item.id} className="flex items-center px-4 py-3 hover:bg-gray-50" data-id="element-3218" data-mp="xnpsjcjlm8ma">
                              <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-md overflow-hidden" data-id="element-3219" data-mp="j10rv7dm1peb">
                                <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-3220" data-mp="e3932m89ijec" />
                              </div>
                              <div className="ml-3 flex-1 min-w-0" data-id="element-3221" data-mp="12050if18ph5g">
                                <p className="text-sm font-medium text-gray-900 truncate" data-id="element-3222" data-mp="11ybqcprf4esp">
                                  {item.name}
                                </p>
                                <div className="flex items-center mt-1" data-id="element-3223" data-mp="1bpojdkoydb0b">
                                  <p className="text-sm font-bold text-[#1E2B55]" data-id="element-3224" data-mp="16qv5hr5r7hzm">
                                    SAR {item.price.toFixed(2)}
                                  </p>
                                  <span className="text-xs text-gray-500 mx-1" data-id="element-3225" data-mp="1fsbga00hdm4d">
                                    ×
                                  </span>
                                  <p className="text-xs text-gray-500" data-id="element-3226" data-mp="dcakefwa1btv">
                                    {item.quantity}
                                  </p>
                                </div>
                              </div>
                              <div className="ml-2 flex-shrink-0" data-id="element-3227" data-mp="r5js8c4ktpaa">
                                <button className="p-1 rounded-full text-gray-400 hover:text-red-500" data-id="element-3228" data-mp="r7wirdoeh7yy">
                                  <TrashIcon className="w-4 h-4" data-id="element-3229" data-mp="w7939rrsu2yd" />
                                </button>
                              </div>
                            </div>)}
                        </div>
                        <div className="px-4 py-3 border-t border-gray-100" data-id="element-3230" data-mp="1a562kvxn0ngq">
                          <div className="flex justify-between items-center mb-3" data-id="element-3231" data-mp="37ufgfo0gdi6e">
                            <span className="text-sm text-gray-600" data-id="element-3232" data-mp="37wiij6evr1rh">
                              Subtotal:
                            </span>
                            <span className="text-base font-bold text-[#1E2B55]" data-id="element-3233" data-mp="34crprwr5wrzg">
                              SAR {currentCartTotal.toFixed(2)}
                            </span>
                          </div>
                          <div className="grid grid-cols-2 gap-2" data-id="element-3234" data-mp="2unqa67oj4cm0">
                            <button onClick={() => {
                      setShowCartMenu(false);
                      navigate('/cart');
                    }} className="text-center py-2 px-4 border border-[#1E2B55] text-[#1E2B55] font-medium rounded-md hover:bg-[#F1F2F6] transition-colors text-sm" data-id="element-3235" data-mp="2qld8pvgtf0v1">
                              View Cart
                            </button>
                            <button onClick={() => {
                      setShowCartMenu(false);
                      navigate('/checkout');
                    }} className="text-center py-2 px-4 bg-[#1E2B55] text-white font-medium rounded-md hover:bg-[#162041] transition-colors text-sm" data-id="element-3236" data-mp="22v95hp7jh34k">
                              Checkout
                            </button>
                          </div>
                        </div>
                      </> : <div className="py-8 text-center" data-id="element-3237" data-mp="kk1324qswgmp">
                        <ShoppingCartIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" data-id="element-3238" data-mp="kkkkzlggb50q" />
                        <p className="text-gray-500" data-id="element-3239" data-mp="picdagl0ct5g">Your cart is empty</p>
                        <button onClick={() => {
                  setShowCartMenu(false);
                  navigate('/');
                }} className="mt-3 inline-block text-sm font-medium text-[#1E2B55] hover:underline" data-id="element-3240" data-mp="asy1z2xvg1nf">
                          Continue Shopping
                        </button>
                      </div>}
                  </div>}
              </div>
            </>}
        </div>
        <AnimatePresence data-id="element-3241" data-mp="2nyuxas6nu4nl">
          {showSearchResults && (searchFocused || searchQuery) && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-x-0 top-[56px] bottom-0 bg-white z-50 overflow-y-auto pb-20" style={{
          top: 'calc(56px + env(safe-area-inset-top))'
        }} data-id="element-3242" data-mp="31tx1navuzlya">
              <div className="p-4 space-y-6" data-id="element-3243" data-mp="2e28bo16if9ki">
                {!searchQuery && recentSearches.length > 0 && <div data-id="element-3244" data-mp="3ufgf7z3xoxgf">
                    <div className="flex items-center justify-between mb-3" data-id="element-3245" data-mp="3uex00q5o4n63">
                      <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-3246" data-mp="3pgvhc6c5mpv5">
                        Recent Searches
                      </h3>
                      <button onClick={() => clearRecentSearches()} className="text-xs text-[#1E2B55] font-bold" data-id="element-3247" data-mp="3a9prc520xla4">
                        Clear All
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-3248" data-mp="3k9qc7vfjk9gf">
                      {recentSearches.map((search, index) => <button key={index} onClick={() => handleSearchSelect(search)} className="flex items-center px-4 py-2 rounded-full bg-gray-100 text-sm text-gray-700" data-id="element-3249" data-mp="3t8ln3ktj67q2" data-mpi={index}>
                          <ClockIcon className="w-3.5 h-3.5 mr-2 text-gray-400" data-id="element-3250" data-mp="ks8n3ktwhzsv" />
                          {search}
                        </button>)}
                    </div>
                  </div>}
                {!searchQuery && <div data-id="element-3251" data-mp="zcuvezb9kdsy">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-3252" data-mp="14e218stuj2wz">
                      Popular Searches
                    </h3>
                    <div className="flex flex-wrap gap-2" data-id="element-3253" data-mp="qdzp7n87z3pn">
                      {POPULAR_SEARCHES.map((search, index) => <button key={index} onClick={() => handleSearchSelect(search)} className="px-4 py-2 bg-blue-50 text-[#1E2B55] rounded-full text-xs font-bold" data-id="element-3254" data-mp="vf6u9eiz9osm" data-mpi={index}>
                          {search}
                        </button>)}
                    </div>
                  </div>}
                {!searchQuery && TRENDING_PRODUCTS.length > 0 && <div data-id="element-3255" data-mp="3txc7gf1n0kfw">
                    <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3" data-id="element-3256" data-mp="2p82vchb9fks">
                      Suggested Products
                    </h3>
                    <div className="space-y-3" data-id="element-3257" data-mp="24fyu5x1pa8l0">
                      {TRENDING_PRODUCTS.map(product => <button key={product.id} onClick={() => handleProductClick(product.id)} className="flex items-center w-full text-left p-3 bg-gray-50 rounded-2xl transition-colors" data-id="element-3258" data-mp="1zlb59x2sbc5w">
                          <div className="w-14 h-14 bg-white rounded-xl overflow-hidden flex-shrink-0 border border-gray-100" data-id="element-3259" data-mp="2nadv3s7kobbh">
                            <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-3260" data-mp="2ebimyakywr5j" />
                          </div>
                          <div className="ml-3 flex-1 min-w-0" data-id="element-3261" data-mp="12usd8hhc5h76">
                            <p className="text-sm font-bold text-gray-900 truncate" data-id="element-3262" data-mp="xx0kxmcs3t2g">
                              {product.name}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5" data-id="element-3263" data-mp="ssoniojrpgri">
                              {product.category}
                            </p>
                            <p className="text-sm font-black text-[#1E2B55] mt-1" data-id="element-3264" data-mp="xqgd3bwxwqsj">
                              {product.price.toFixed(2)} SAR
                            </p>
                          </div>
                          <ChevronRightIcon className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" data-id="element-3265" data-mp="16k3af6gsq23u" />
                        </button>)}
                    </div>
                  </div>}
                {searchQuery && <div data-id="element-3266" data-mp="1lhvnzt2s8gfe">
                    <div className="flex items-center justify-between mb-4" data-id="element-3267" data-mp="1lh3poahsw4lf">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-wider" data-id="element-3268" data-mp="3iankvwj9b0cg">
                        Results for "{searchQuery}"
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-3269" data-mp="1hkg43gv6fhab">
                        {TRENDING_PRODUCTS.filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase())).length}{' '}
                        items found
                      </p>
                    </div>
                    <div className="space-y-3" data-id="element-3270" data-mp="xwyy5r1cbwof">
                      {TRENDING_PRODUCTS.filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase())).slice(0, searchResultsLimit).map(product => <button key={product.id} onClick={() => handleProductClick(product.id)} className="flex items-center w-full text-left p-3 bg-gray-50 rounded-2xl transition-colors" data-id="element-3271" data-mp="f4wpb1uxxkoq">
                            <div className="w-14 h-14 bg-white rounded-xl overflow-hidden flex-shrink-0 border border-gray-100" data-id="element-3272" data-mp="2qlzw308afgo8">
                              <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-3273" data-mp="2crp5edcqdcfj" />
                            </div>
                            <div className="ml-3 flex-1 min-w-0" data-id="element-3274" data-mp="39jixbhoi7v03">
                              <p className="text-sm font-bold text-gray-900 truncate" data-id="element-3275" data-mp="2vkt3cawzebfy">
                                {product.name}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5" data-id="element-3276" data-mp="30l7bdsyl1ldz">
                                {product.category}
                              </p>
                              <p className="text-sm font-black text-[#1E2B55] mt-1" data-id="element-3277" data-mp="1p50krjwc75v8">
                                {product.price.toFixed(2)} SAR
                              </p>
                            </div>
                            <ChevronRightIcon className="w-5 h-5 text-gray-400 ml-2 flex-shrink-0" data-id="element-3278" data-mp="111axogcmq4zl" />
                          </button>)}
                    </div>
                    {TRENDING_PRODUCTS.filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase())).length > searchResultsLimit && <div className="mt-6" data-id="element-3279" data-mp="dandavlj1mw5">
                        <button onClick={() => setSearchResultsLimit(searchResultsLimit + 5)} className="w-full py-3 bg-gray-100 text-gray-900 font-bold rounded-xl text-sm" data-id="element-3280" data-mp="dbq5r7yhzxe6">
                          Show More Results
                        </button>
                      </div>}
                    {TRENDING_PRODUCTS.filter(product => product.name.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && <div className="py-6 px-2" data-id="element-3281" data-mp="2oys689n6l04m">
                        <div className="flex items-start gap-3 mb-4" data-id="element-3282" data-mp="2oxh7io8leikd">
                          <div className="w-12 h-12 rounded-full bg-[#EEF0F6] flex items-center justify-center flex-shrink-0" data-id="element-3283" data-mp="1rbtiwpidp4je">
                            <span className="text-2xl" data-id="element-3284" data-mp="1wbpblslml9jn">🧑‍⚕️</span>
                          </div>
                          <div data-id="element-3285" data-mp="217ehedo8nmoe">
                            <h3 className="text-base font-bold text-gray-900" data-id="element-3286" data-mp="25a1ccng7hnpt">
                              No Matching Search Results
                            </h3>
                            <p className="text-sm text-gray-500 mt-1" data-id="element-3287" data-mp="3cpxnmt8rs0h1">
                              The product doesn't seem to be available right
                              now. Don't worry — you can try other keywords, or
                              contact our pharmacist for help.
                            </p>
                          </div>
                        </div>
                        <ul className="text-sm text-gray-600 space-y-1.5 mb-5 pl-5 list-disc" data-id="element-3288" data-mp="gcynz3h7u9t2">
                          <li data-id="element-3289" data-mp="bj4a4goakp1w">
                            Make sure the product name is spelled correctly.
                          </li>
                          <li data-id="element-3290" data-mp="kd0r7h2lnbag">Try a shorter or more general keyword.</li>
                        </ul>
                        <div className="border-t border-gray-200 pt-4 mb-5" data-id="element-3291" data-mp="za9laijaet5v">
                          <h4 className="text-sm font-bold text-gray-900 mb-3" data-id="element-3292" data-mp="z86y069ire4u">
                            Contact Customer Service / Ask a Pharmacist
                          </h4>
                          <div className="bg-gray-50 rounded-xl p-3 flex items-center gap-3" data-id="element-3293" data-mp="31266kjv9bhdu">
                            <div className="w-9 h-9 rounded-full bg-[#EEF0F6] flex items-center justify-center flex-shrink-0" data-id="element-3294" data-mp="2w44h9h78mltx">
                              <span className="text-lg" data-id="element-3295" data-mp="1bmvabwehvobj">💬</span>
                            </div>
                            <div className="flex-1 min-w-0" data-id="element-3296" data-mp="1glexg04tr65h">
                              <p className="text-sm font-semibold text-gray-900" data-id="element-3297" data-mp="11twkycb6btqv">
                                Need quick help?
                              </p>
                              <p className="text-xs text-gray-500" data-id="element-3298" data-mp="sthxp7eng4kq">
                                Chat with an Innova pharmacist or leave us your
                                question.
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3" data-id="element-3299" data-mp="nuoi99fsy3hx">
                            <a href="https://wa.me/966500000000" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-3300" data-mp="nv6o8xs9nw31">
                              WhatsApp
                            </a>
                            <a href="tel:+966500000000" className="flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-3301" data-mp="3txgpit6y3das">
                              Direct Call
                            </a>
                          </div>
                        </div>
                      </div>}
                  </div>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Legacy sidebar disabled: mobile navigation now opens the /menu page. */}
      <AnimatePresence data-id="element-3302" data-mp="2nc60aavtsxht">
        {false && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black bg-opacity-50 z-[60]" onClick={() => setShowCategoriesMenu(false)} data-id="element-3303" data-mp="2frll6cabdgz0" />
            <motion.div initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed inset-0 w-full min-h-[100dvh] bg-white z-[70] shadow-xl overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]" data-id="element-3304" data-mp="2ypxy894cjazv">
              <div className="sticky top-0 z-10 flex items-center justify-between p-4 border-b border-gray-200 bg-white" data-id="element-3305" data-mp="18bxe79zt517d">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8onJCNEDDvmSU1TZkPymCw/English.png" alt="Innova Pharmacy" className="h-10" data-id="element-3306" data-mp="byr6rccb75z" />
                <button onClick={() => setShowCategoriesMenu(false)} className="p-2 rounded-full hover:bg-gray-100" data-id="element-3307" data-mp="3rjufrt58yqlo">
                  <XIcon className="w-5 h-5 text-gray-500" data-id="element-3308" data-mp="3ct3qarr4o3lq" />
                </button>
              </div>
              <div className="pb-[max(8rem,env(safe-area-inset-bottom))]" data-id="element-3309" data-mp="3hnrfkstyh21d">
                <div className="p-4 bg-gray-50 border-b border-gray-200" data-id="element-3310" data-mp="33wcv0hkegss7">
                  <div className="flex items-center space-x-3" data-id="element-3311" data-mp="38uo42q2vez9d">
                    <div className="w-10 h-10 rounded-full bg-[#1E2B55] flex items-center justify-center text-white" data-id="element-3312" data-mp="2twm04erxgbrl">
                      <UserIcon className="w-5 h-5" data-id="element-3313" data-mp="2yvosozqq0pio" />
                    </div>
                    <div data-id="element-3314" data-mp="2f9dhwu07b664">
                      <p className="text-sm font-medium" data-id="element-3315" data-mp="2faom2uxk6fxr">Hello, Guest</p>
                      <div className="flex space-x-2 mt-1" data-id="element-3316" data-mp="2b8biod6aau45">
                        <button onClick={openAuthModal} className="text-xs font-medium text-[#1E2B55] hover:text-[#162041]" data-id="element-3317" data-mp="2b9uxdzcakx3t">
                          Sign In
                        </button>
                        <span className="text-xs text-gray-400" data-id="element-3318" data-mp="2y7g3szxgcmzp">|</span>
                        <button onClick={openAuthModal} className="text-xs font-medium text-[#1E2B55] hover:text-[#162041]" data-id="element-3319" data-mp="2u75zdrpb64my">
                          Register
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-3320" data-mp="3hue3w7ewx9nx">
                  <button onClick={() => setLanguageBottomSheetOpen(true)} className="flex items-center justify-between w-full" data-id="element-3321" data-mp="3cyoy3mcauwj6">
                    <div className="flex items-center space-x-2" data-id="element-3322" data-mp="1f8rvhxd6fmcw">
                      <GlobeIcon className="w-4 h-4 text-gray-500" data-id="element-3323" data-mp="1k0jsapmxeobh" />
                      <span className="text-sm" data-id="element-3324" data-mp="1k1bi1jtudrtu">Language</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-700 bg-gray-100 px-2 py-1 rounded" data-id="element-3325" data-mp="1oz396bmnx9wz">
                      {language === 'en' ? 'English' : 'Arabic'}
                      <ChevronRightIcon className="w-4 h-4 ml-1" data-id="element-3326" data-mp="wc0gw18kxvf6" />
                    </div>
                  </button>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-3327" data-mp="11c4yjul84w6z">
                  <LoyaltyCard progress={loyaltyProgress} onJoinNow={() => {
                setShowCategoriesMenu(false);
                setLoyaltyBottomSheetOpen(true);
              }} loyaltyData={loyaltyData} data-id="element-3328" data-mp="16a6kcna1b9m5" />
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-3329" data-mp="631d3m083cyq">
                  <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3330" data-mp="12wvttrhqe7g">MENU</h3>
                  <ul className="space-y-1" data-id="element-3331" data-mp="3sfzttk7l6t34">
                    <li className="py-1" data-id="element-3332" data-mp="3seoupxp369ic">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/categories');
                  }} className="group w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-bold px-4 py-3 rounded-lg shadow-md text-center flex items-center justify-center gap-2" data-id="element-3333" data-mp="3nhq3po0pyiod">
                        All Categories
                      </button>
                    </li>
                    <li data-id="element-3334" data-mp="17p987ewwh6yi">
                      <button onClick={() => handleCategoryClick('best-sellers')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3335" data-mp="17pixt1e19c3o">
                        <TrendingUpIcon className="w-4 h-4 mr-3" data-id="element-3336" data-mp="31yo5k508ydtz" />
                        <span className="text-sm" data-id="element-3337" data-mp="31zz861hyhfji">Best Sellers</span>
                        <span className="ml-auto text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded-full" data-id="element-3338" data-mp="2nat9qfqxztco">
                          HOT
                        </span>
                      </button>
                    </li>
                    <li data-id="element-3339" data-mp="3fvun5w98i6hm">
                      <button onClick={() => handleCategoryClick('new-arrivals')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3340" data-mp="3kwqlluath0fn">
                        <ShoppingBagIcon className="w-4 h-4 mr-3" data-id="element-3341" data-mp="3ax9j1lqv32nw" />
                        <span className="text-sm" data-id="element-3342" data-mp="1ukb7fgy1lw0b">New Arrivals</span>
                        <span className="ml-auto text-xs bg-[#1E2B55] text-white px-1.5 py-0.5 rounded-full" data-id="element-3343" data-mp="1zjnw878h5d5j">
                          NEW
                        </span>
                      </button>
                    </li>
                    <li data-id="element-3344" data-mp="cnr3pfp712f9">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/offers');
                  }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3345" data-mp="cm6apuij1rji">
                        <TagIcon className="w-4 h-4 mr-3" data-id="element-3346" data-mp="3q1uhcaot7okk" />
                        <span className="text-sm" data-id="element-3347" data-mp="3q3fco2iy7bjh">Deals & Offers</span>
                        <span className="ml-auto text-xs bg-red-500 text-white px-1.5 py-0.5 rounded-full" data-id="element-3348" data-mp="7o4nqyhzd82r">
                          SALE
                        </span>
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-3349" data-mp="37bcom63z8sz7">
                  <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3350" data-mp="37b4c4fbrnpoj">
                    SHOP BY CATEGORY
                  </h3>
                  <ul className="space-y-1" data-id="element-3351" data-mp="4zuhucti278u">
                    <li data-id="element-3352" data-mp="50nfmoahdmeu">
                      <button onClick={() => handleCategoryClick('medicines')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3353" data-mp="5048m2mdcifl">
                        <span className="text-sm" data-id="element-3354" data-mp="j05a9kagjtw4">Medicines (OTC)</span>
                        <ChevronRightIcon className="w-4 h-4 ml-auto" data-id="element-3355" data-mp="ixsqr2p1xbgy" />
                      </button>
                    </li>
                    <li data-id="element-3356" data-mp="sowypigb7w2a">
                      <button onClick={() => handleCategoryClick('vitamins-supplements')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3357" data-mp="sqq5cxqxagaa">
                        <span className="text-sm" data-id="element-3358" data-mp="16pps1zuvkl3t">Vitamins & Supplements</span>
                        <ChevronRightIcon className="w-4 h-4 ml-auto" data-id="element-3359" data-mp="1gu66qpwmbw61" />
                      </button>
                    </li>
                    <li data-id="element-3360" data-mp="1bsqm2yycrro6">
                      <button onClick={() => handleCategoryClick('personal-care')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3361" data-mp="1bweefus2a81b">
                        <span className="text-sm" data-id="element-3362" data-mp="1zib6zne2gz03">Personal Care</span>
                        <ChevronRightIcon className="w-4 h-4 ml-auto" data-id="element-3363" data-mp="1uk1bfbofjwdy" />
                      </button>
                    </li>
                    <li data-id="element-3364" data-mp="1psrpom605d7c">
                      <button onClick={() => handleCategoryClick('mother-baby')} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3365" data-mp="1ps0506dy0zvu">
                        <span className="text-sm" data-id="element-3366" data-mp="2divpsjjzeutl">Mother & Baby</span>
                        <ChevronRightIcon className="w-4 h-4 ml-auto" data-id="element-3367" data-mp="2dhkpwutnq77r" />
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-3368" data-mp="7thnc6s8m5y4">
                  <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3369" data-mp="31g769ry7v2t">
                    BROWSE BY CONDITION
                  </h3>
                  <div className="grid grid-cols-2 gap-3" data-id="element-3370" data-mp="3v5innku2egtx">
                    {conditions.map((condition, index) => <button key={index} onClick={() => handleConditionClick(condition.slug)} className="inline-flex items-center gap-1.5 border border-gray-200 bg-gray-50 text-sm text-gray-800 px-3 py-2 rounded-full whitespace-nowrap" data-id="element-3371" data-mp="3q4ky2n6q6kie" data-mpi={index}>
                        <span className="flex-shrink-0" data-id="element-3372" data-mp="1ehwoy9qz6nsu">{condition.icon}</span>
                        <span className="font-medium text-xs" data-id="element-3373" data-mp="1aftardcx953v">
                          {condition.name}
                        </span>
                      </button>)}
                  </div>
                </div>
                <div className="p-4 border-b border-gray-200" data-id="element-3374" data-mp="2xbhepfqpwm34">
                  <h3 className="text-xs font-bold text-gray-500 mb-3" data-id="element-3375" data-mp="3237rrth1hny8">
                    MY ACCOUNT
                  </h3>
                  <ul className="space-y-1" data-id="element-3376" data-mp="3652tgz2vu3cj">
                    <li data-id="element-3377" data-mp="366xgcixps5j9">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/account');
                  }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3378" data-mp="29ek63wsdowk5">
                        <UserIcon className="w-4 h-4 mr-3" data-id="element-3379" data-mp="2nf4o4qqgyocl" />
                        <span className="text-sm" data-id="element-3380" data-mp="3tunoigcb8r26">My Account</span>
                      </button>
                    </li>
                    <li data-id="element-3381" data-mp="2nb7c1bfgdm9">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/account/orders');
                  }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3382" data-mp="2mjcispnmjx1">
                        <PackageIcon className="w-4 h-4 mr-3" data-id="element-3383" data-mp="23qljseaaxi5e" />
                        <span className="text-sm" data-id="element-3384" data-mp="23o91u14jn7sc">My Orders</span>
                      </button>
                    </li>
                    <li data-id="element-3385" data-mp="1b1od78fgd9sh">
                      <button onClick={() => {
                    setShowCategoriesMenu(false);
                    navigate('/account/wishlist');
                  }} className="flex items-center py-2 px-1 hover:text-[#1E2B55] transition-colors w-full text-left" data-id="element-3386" data-mp="1b2fwbjrv5f1v">
                        <HeartIcon className="w-4 h-4 mr-3" data-id="element-3387" data-mp="x86qjergzgy8" />
                        <span className="text-sm" data-id="element-3388" data-mp="sa572sq4tfm8">My Wishlist</span>
                        {currentWishlistCount > 0 && <span className="ml-auto bg-[#1E2B55] text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center" data-id="element-3389" data-mp="33x7gijwslsot">
                            {currentWishlistCount}
                          </span>}
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="p-4 border-t border-gray-200 pb-32" data-id="element-3390" data-mp="3moruogw6hyip">
                  <button onClick={() => {
                setShowCategoriesMenu(false);
              }} className="flex items-center text-red-500 font-medium hover:text-red-600 w-full" data-id="element-3391" data-mp="3ro4fyx7eixj6">
                    <LogOutIcon className="w-4 h-4 mr-2" data-id="element-3392" data-mp="3ilv689jyj86u" />
                    <span className="text-sm" data-id="element-3393" data-mp="228n6kmpnm4g7">Sign Out</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>

      <LanguageBottomSheet isOpen={languageBottomSheetOpen} onClose={() => setLanguageBottomSheetOpen(false)} currentLanguage={language} onLanguageChange={handleLanguageChange} data-id="element-3394" data-mp="2mhmr8ym7c26w" />

      <AuthBottomSheet isOpen={authBottomSheetOpen} onClose={() => setAuthBottomSheetOpen(false)} onSuccess={handleAuthSuccess} data-id="element-3395" data-mp="2crbmxaylvqzh" />

      <LoyaltyOnboardingBottomSheet isOpen={loyaltyBottomSheetOpen} onClose={() => setLoyaltyBottomSheetOpen(false)} onComplete={handleLoyaltyComplete} data-id="element-3396" data-mp="28ooqewqzphvy" />
    </>;
}